import * as dateFns from 'date-fns';
import type { HistoricVesselPoint } from '../maritime-menu-options/history-panel/historic-vessel-point.model';
import { mapVesselSubTypeToSuperType } from '../models/vessels/all-ship-types';
import type { LocationMessage } from '../models/vessels/internal-ais-api-v3.model';
import { Vessel, VesselSource } from '../models/vessels/vessel.model';
import {
  DefaultHistoryDuration,
  DefaultHistoryFrequency,
} from '../settings/user-preferences.slice';
import store from '../store';
import retryAsync from '../utils/retry';
import { wrapRequest } from './base';
import { getLocationMessages } from './internal-ais-api-v3';

/**
 * Convert a flat array of v2 `LocationMessage` records to the internal
 * `HistoricVesselPoint` shape.  Records without a position (no `latitude` or
 * `longitude`) are silently dropped.
 */
export const mapLocationMessagesToHistoricVesselPoints = (
  messages: LocationMessage[]
): HistoricVesselPoint[] =>
  messages.reduce<HistoricVesselPoint[]>((acc, msg) => {
    if (msg.latitude === undefined || msg.longitude === undefined) {
      return acc;
    }

    acc.push({
      id: `${msg.vesselUid}-${msg.posDt}`,
      vessel_id: String(msg.vesselUid),
      name: msg.vesselName ?? '',
      imo: msg.imo,
      mmsi: msg.mmsi,
      timestamp: msg.posDt * 1000,
      created_at: msg.insertDt !== undefined ? msg.insertDt * 1000 : undefined,
      latitude: msg.latitude,
      longitude: msg.longitude,
      heading: msg.heading,
      speed: msg.sog ?? 0,
      course: msg.cog ?? 0,
      rot: msg.rot ?? 0,
      status: msg.navStatus,
      callsign: msg.callsign ?? '',
      vessel_sub_type: msg.vesselType,
      vessel_super_type: mapVesselSubTypeToSuperType(msg.vesselType),
      flag: msg.flag,
      collection_type: msg.posSrc ?? '',
      maneuver: 0,
      source: VesselSource.AIS,
      destination: msg.destination,
      eta: msg.eta,
      draught: msg.draught,
    });

    return acc;
  }, []);

const getSampleInterval = (
  default_history: { frequency: DefaultHistoryFrequency } | undefined
): string => {
  if (default_history?.frequency === DefaultHistoryFrequency.HOURLY) {
    return '1h';
  }
  if (default_history?.frequency === DefaultHistoryFrequency.DAILY) {
    return '1d';
  }
  return '10m';
};

/**
 * Add one sample-interval worth of time to a Date so that the API's upper
 * bound (which appears to be exclusive) returns the point that sits exactly
 * at `endDate`.  After fetching we filter any messages with posDt > endDate
 * back out, so callers always see at most `endDate` as the latest point.
 */
const addOneSampleInterval = (date: Date, sampleInterval: string): Date => {
  if (sampleInterval === '1d') return dateFns.addDays(date, 1);
  if (sampleInterval === '1h') return dateFns.addHours(date, 1);
  // default: '10m'
  return dateFns.addMinutes(date, 10);
};

// Max identifiers (MMSIs or IMOs) per parallel API request.
const MMSI_BATCH_SIZE = 500;
const VESSEL_REQUEST_BATCH_SIZE = 500;

/**
 * Fetch location messages for a potentially large set of MMSIs and/or IMOs,
 * automatically splitting into parallel batches of at most
 * VESSEL_REQUEST_BATCH_SIZE identifiers per request.  MMSIs and IMOs are
 * batched independently so the API receives correctly-typed arrays.
 * Each batch handles cursor-pagination internally.
 *
 * Returns the flat union of all messages from all batches.
 */
export const fetchLocationMessagesBatched = async ({
  mmsis = [],
  imos = [],
  start_datetime,
  end_datetime,
  sample_interval,
  callerFeature,
}: {
  mmsis?: number[];
  imos?: number[];
  start_datetime: string;
  end_datetime: string;
  sample_interval: string;
  callerFeature: string;
}): Promise<LocationMessage[]> => {
  const batches: { mmsis?: number[]; imos?: number[] }[] = [];

  for (let i = 0; i < mmsis.length; i += VESSEL_REQUEST_BATCH_SIZE) {
    batches.push({ mmsis: mmsis.slice(i, i + VESSEL_REQUEST_BATCH_SIZE) });
  }
  for (let i = 0; i < imos.length; i += VESSEL_REQUEST_BATCH_SIZE) {
    batches.push({ imos: imos.slice(i, i + VESSEL_REQUEST_BATCH_SIZE) });
  }

  if (batches.length === 0) return [];

  const fetchOneBatch = async (ids: {
    mmsis?: number[];
    imos?: number[];
  }): Promise<LocationMessage[]> => {
    const messages: LocationMessage[] = [];
    let cursor: string | undefined;
    do {
      // biome-ignore lint/performance/noAwaitInLoops: cursor-based pagination requires sequential requests
      const response = await getLocationMessages({
        callerFeature,
        body: {
          ...ids,
          start_datetime,
          end_datetime,
          sample_interval,
          ...(cursor ? { cursor } : {}),
        },
      });
      if (Array.isArray(response.data)) {
        messages.push(...response.data);
      }
      cursor = response.cursor;
    } while (cursor);
    return messages;
  };

  const results = await Promise.all(batches.map(fetchOneBatch));
  return results.flat();
};

/**
 * Fetch the single most-recent AIS position for each of the given MMSIs,
 * returning a Map keyed by MMSI string for O(1) lookup.
 *
 * MMSIs are split into batches of up to 500 and all batches are requested in
 * parallel; the results are merged into a single Map.
 */
export const getMostRecentVesselLocationsByMmsi = async (
  mmsis: number[],
  callerFeature: string
): Promise<Map<string, LocationMessage>> => {
  const batches: number[][] = [];
  for (let i = 0; i < mmsis.length; i += MMSI_BATCH_SIZE) {
    batches.push(mmsis.slice(i, i + MMSI_BATCH_SIZE));
  }

  const responses = await Promise.all(
    batches.map((batch) =>
      getLocationMessages({
        callerFeature,
        body: {
          mmsis: batch,
          most_recent: true,
        },
      })
    )
  );

  const map = new Map<string, LocationMessage>();
  for (const response of responses) {
    const data = response.data;
    if (Array.isArray(data)) {
      for (const msg of data) {
        map.set(String(msg.mmsi), msg);
      }
    }
  }
  return map;
};

/**
 * Fetch AIS position history for a single vessel up to a given end date,
 * using the location_messages_v2 API.
 *
 * The window length and sample interval are driven by the user's history preferences:
 * - end   = endDate (caller decides — e.g. vessel's last known position, or event time + offset)
 * - start = end minus 7 days (or 30 days when user prefs are set to MONTH)
 */
export const getVesselTrackPointsV3 = async (
  mmsi: string,
  endDate: Date,
  callerFeature = 'vessel-track'
): Promise<HistoricVesselPoint[]> => {
  const { default_history } = store.getState().userPreferences.userPreferences;

  const days =
    default_history?.duration === DefaultHistoryDuration.MONTH ? 30 : 7;
  const sampleInterval = getSampleInterval(default_history);
  const startDate = dateFns.subDays(endDate, days);

  const startDatetime = startDate.toISOString();
  // Request slightly beyond endDate so the API (exclusive upper bound) returns
  // the point that sits exactly at endDate.
  const bufferedEndDate = addOneSampleInterval(endDate, sampleInterval);
  const endDatetime = bufferedEndDate.toISOString();

  const allMessages: LocationMessage[] = [];
  let cursor: string | undefined;

  do {
    // biome-ignore lint/performance/noAwaitInLoops: cursor-based pagination requires sequential requests
    const response = await getLocationMessages({
      callerFeature,
      body: {
        mmsis: [parseInt(mmsi, 10)],
        start_datetime: startDatetime,
        end_datetime: endDatetime,
        sample_interval: sampleInterval,
        ...(cursor ? { cursor } : {}),
      },
    });

    const data = response.data;
    if (Array.isArray(data)) {
      allMessages.push(...data);
    }
    cursor = response.cursor;
  } while (cursor);

  // Filter out any messages that fall in the buffer zone (after endDate).
  const endMs = endDate.getTime();
  const filteredMessages = allMessages.filter(
    (msg) => msg.posDt * 1000 <= endMs
  );

  const points = mapLocationMessagesToHistoricVesselPoints(filteredMessages);
  return points.sort((a, b) => a.timestamp - b.timestamp);
};

export const initialiseByV3LocationWebsocket = async (
  geometry: object,
  token: string,
  callerFeature: string,
  hours?: number,
  startDate?: Date,
  endDate?: Date
): Promise<void> => {
  if (!hours && (!startDate || !endDate)) {
    throw new Error(
      "Either 'hours' or both 'startDate' and 'endDate' must be provided"
    );
  }

  let startDatetime: string | undefined;
  let endDatetime: string | undefined;

  if (hours) {
    startDatetime = dateFns.subHours(new Date(), hours).toISOString();
    endDatetime = new Date().toISOString();
  } else if (startDate && endDate) {
    startDatetime = startDate.toISOString();
    endDatetime = endDate.toISOString();
  }

  const response = await retryAsync(
    () =>
      wrapRequest('post', 'geonius', `/v2/ais-data`, {
        headers: {
          'x-caller-product': 'Geollect',
          'x-caller-feature': callerFeature,
        },
        body: {
          geometry,
          start_datetime: startDatetime,
          end_datetime: endDatetime,
          token,
        },
        queryStringParameters: {
          upstream_path: 'websocket/location_messages_v2',
        },
      }),
    3,
    1000
  );

  if (response.errors) {
    throw new Error(
      `Error initialising websocket: ${response.errors.join(', ')}`
    );
  }
};

/**
 * Initialise a v3 AIS WebSocket query filtered by vessel identifiers
 * (MMSIs and/or IMOs) rather than a geographic area.
 *
 * Calls the same backend proxy endpoint as the geometry variant but sends
 * `mmsis`/`imos` in place of `geometry`.  The token must be obtained
 * beforehand via `getV3Token` — each token is single-use.
 *
 * @param mmsis - Array of MMSI numbers to query (may be empty if imos provided).
 * @param imos  - Array of IMO numbers to query (may be empty if mmsis provided).
 * @param token - One-time JWT from `getV3Token`.
 * @param startDate - Start of the time window (inclusive).
 * @param endDate   - End of the time window (exclusive).
 * @param sampleInterval - Sampling window per vessel, e.g. "10m", "1h", "1d".
 * @param callerFeature  - Name of the calling feature for logging/monitoring.
 */
export const initialiseByV3LocationWebsocketByIdentifiers = async ({
  mmsis,
  imos,
  token,
  startDate,
  endDate,
  sampleInterval,
  callerFeature,
}: {
  mmsis: number[];
  imos: number[];
  token: string;
  startDate: Date;
  endDate: Date;
  sampleInterval: string;
  callerFeature: string;
}): Promise<void> => {
  const response = await retryAsync(
    () =>
      wrapRequest('post', 'geonius', `/v2/ais-data`, {
        headers: {
          'x-caller-product': 'Geollect',
          'x-caller-feature': callerFeature,
        },
        body: {
          ...(mmsis.length > 0 ? { mmsis } : {}),
          ...(imos.length > 0 ? { imos } : {}),
          start_datetime: startDate.toISOString(),
          end_datetime: endDate.toISOString(),
          sample_interval: sampleInterval,
          token,
        },
        queryStringParameters: {
          upstream_path: 'websocket/location_messages_v2',
        },
      }),
    3,
    1000
  );

  if (response.errors) {
    throw new Error(
      `Error initialising identifier websocket: ${response.errors.join(', ')}`
    );
  }
};

/**
 * Convert a `LocationMessage` from the v3 AIS API to a `Vessel`.
 * Returns `null` when the message has no position (latitude/longitude absent).
 */
export const locationMessageToVessel = (
  msg: LocationMessage | null | undefined
): Vessel | null => {
  if (!msg || msg.latitude === undefined || msg.longitude === undefined) {
    return null;
  }

  return {
    vessel_id: String(msg.vesselUid),
    name: msg.vesselName ?? '',
    imo: msg.imo,
    mmsi: msg.mmsi,
    latitude: msg.latitude,
    longitude: msg.longitude,
    heading: msg.heading,
    speed: msg.sog,
    course: msg.cog ?? 0,
    callsign: msg.callsign ?? '',
    vessel_sub_type: msg.vesselType,
    timestamp: msg.posDt * 1000,
    vessel_super_type: mapVesselSubTypeToSuperType(msg.vesselType),
    source: VesselSource.AIS,
    ...(msg.length !== undefined && {
      dimensions: {
        length: msg.length,
        width: msg.width,
      },
    }),
  };
};
